import React from 'react';
import { Badge, Box, Flex, Text, Tooltip, VStack } from '@chakra-ui/react';
import { Selection, SelectionOutcome, SelectionStatus, Side } from '../../types/marketTypes';
import { linePickLabel, quotedLine, SelectionLine } from '../../utils/marketUtils';

interface MarketSelectionRowProps {
  selection: Selection;
  compact?: boolean;
  /** What to call the selection; its own name by default. */
  label?: string;
}

const statusColor = (status: SelectionStatus) => {
  switch (status) {
    case SelectionStatus.OPEN:
      return 'green';
    case SelectionStatus.SUSPENDED:
      return 'orange';
    case SelectionStatus.CLOSED:
      return 'gray';
    default:
      return 'gray';
  }
};

/**
 * One selection of a market: its name, its own status and result, and every price quoted on it. Each
 * selection carries these on its own, which is what lets one line of a market be resulted while the
 * rest of the market still trades.
 */
export const MarketSelectionRow: React.FC<MarketSelectionRowProps> = ({
  selection,
  compact = false,
  label,
}) => (
  <VStack align="stretch" spacing={0} mt={compact ? 0 : 1}>
    <Flex
      direction={{ base: compact ? 'row' : 'column', sm: 'row' }}
      justify="space-between"
      align={{ base: 'flex-start', sm: 'center' }}
      gap={{ base: 1, sm: 0 }}
    >
      <Tooltip
        label={label ? `${selection.name} — Selection ID: ${selection.id}` : `Selection ID: ${selection.id}`}
        placement="top"
      >
        <Text fontSize="xs" fontWeight="semibold" cursor="help">
          {label ?? selection.name}
        </Text>
      </Tooltip>
      {/* The status and its result are one reading, so they never wrap apart and never shrink: a long
          pick name wraps instead. `Patrick Franziska to win point 15` used to push LOSE onto its own
          line, leaving the badge a trader reads the line's state from split across two rows. */}
      <Flex gap={1} flexWrap="nowrap" flexShrink={0} align="center">
        <Tooltip label={`Selection Status: ${selection.status}`}>
          <Badge
            colorScheme={statusColor(selection.status)}
            fontSize="0.5rem"
            variant="solid"
          >
            {selection.status}
          </Badge>
        </Tooltip>
        {selection.outcome && (
          <Badge
            colorScheme={
              selection.outcome === SelectionOutcome.WIN
                ? 'green'
                : selection.outcome === SelectionOutcome.LOSE
                  ? 'red'
                  : selection.outcome === SelectionOutcome.VOID
                    ? 'red'
                    : 'gray'
            }
            fontSize="0.5rem"
          >
            {selection.outcome}
          </Badge>
        )}
      </Flex>
    </Flex>
    {selection.prices.length > 0 && (
      <Flex
        flexWrap="wrap"
        gap={{ base: 2, md: 4 }}
        pl={compact ? 1 : 2}
        align="center"
      >
        {selection.prices.map((price) => (
          <Flex key={price.id} gap={compact ? 0.5 : 1} align="center" fontSize={compact ? "xs" : "md"} mr={compact ? 1 : 2}>
            <Badge
              size="sm"
              variant="outline"
              colorScheme={price.side === Side.BACK ? 'blue' : 'red'}
              fontSize={compact ? "0.6rem" : "0.7rem"}
            >
              {price.side}
            </Badge>
            <Flex align="center">
              <Text fontWeight="semibold">{price.price.toFixed(2)}</Text>
              {price.priceChange === 'up' && (
                <Box ml={1} color="green.500" fontWeight="bold">
                  <Text as="span" fontSize={compact ? "xs" : "sm"}>▲</Text>
                </Box>
              )}
              {price.priceChange === 'down' && (
                <Box ml={1} color="red.500" fontWeight="bold">
                  <Text as="span" fontSize={compact ? "xs" : "sm"}>▼</Text>
                </Box>
              )}
            </Flex>
            {price.probability !== undefined && (
              <Text color="gray.600" fontSize={compact ? "xs" : "sm"} ml={1}>
                ({(price.probability * 100).toFixed(1)}%)
              </Text>
            )}
            {price.outcomeValue && (
              <Text color="gray.500">({price.outcomeValue})</Text>
            )}
          </Flex>
        ))}
      </Flex>
    )}
  </VStack>
);

interface MarketSelectionLinesProps {
  lines: SelectionLine[];
  compact?: boolean;
}

/**
 * The selections of a market that offers several lines at once, one box per line, ordered by the line
 * itself. The two picks on a line belong together — they are one bet seen from either side — and a line
 * settles and suspends on its own, so the box is where a trader reads its state.
 *
 * <p>The lines keep their order as the market rolls, so no box moves under the cursor; the one being
 * quoted is picked out by its border instead.
 */
export const MarketSelectionLines: React.FC<MarketSelectionLinesProps> = ({ lines, compact = false }) => {
  const quoted = quotedLine(lines);

  return (
    <VStack align="stretch" spacing={2} mt={2}>
      {lines.map((line) => {
        const isQuoted = quoted?.value === line.value;
        return (
          <Box
            key={line.value}
            borderWidth={isQuoted ? '2px' : '1px'}
            borderColor={isQuoted ? 'green.400' : undefined}
            bg={isQuoted ? 'green.50' : undefined}
            borderRadius="md"
            p={2}
          >
            <Text fontSize="sm" fontWeight="bold" mb={1} color="gray.700">
              {line.label}
            </Text>
            {line.selections.map((selection) => (
              <MarketSelectionRow
                key={selection.id}
                selection={selection}
                compact={compact}
                label={linePickLabel(line.marketType, selection)}
              />
            ))}
          </Box>
        );
      })}
    </VStack>
  );
};
